import React, { useState } from 'react'
import { useExercise } from '../ExerciseProvider'
import { ClearSelectionsButton } from '~/components/shared/Buttons'
import { DndContext } from '@dnd-kit/core'
import { Draggable, Droppable } from '~/components/shared/DragAndDrop'
import { cn } from '~/lib/utils'
import { useVolunteeringExercise } from './VolunteeringExerciseProvider'

const VolunteeringExercisePageOne = () => {
  const { updateResponse, exerciseIndex } = useExercise()
  const { rankedGoals, setRankedGoals, goals, pageOneTranslations } =
    useVolunteeringExercise()
  const handleClearSelections = () => {
    const newGoals = goals.map((goal) => ({
      ...goal,
      rank: null,
    }))
    setRankedGoals(newGoals)
    updateResponse(exerciseIndex, 'rankedGoals', newGoals)
  }

  const rankedGoalsProps = {
    pageOneTranslations,
    rankedGoals,
    setRankedGoals,
  }

  return (
    <div className="flex flex-col gap-8">
      <RankGoals {...rankedGoalsProps} />
      <div className="flex justify-end">
        <ClearSelectionsButton onClick={handleClearSelections}>
          {pageOneTranslations.clear_selections}
        </ClearSelectionsButton>
      </div>
    </div>
  )
}

export default VolunteeringExercisePageOne

const RankGoals = ({ pageOneTranslations, rankedGoals, setRankedGoals }) => {
  const { updateResponse, exerciseIndex, setContinueButtonDisabled } =
    useExercise()
  const goals = pageOneTranslations.goals
  const [dragging, setDragging] = useState(false)

  const unrankedGoals = rankedGoals.filter((g) => g.rank === null)

  const getRankedGoal = (rank) => {
    const rankedGoal = rankedGoals.find((i) => parseInt(i.rank) === rank)
    return rankedGoal ? rankedGoal : null
  }

  const getRank = (slug) => {
    const rankedGoal = rankedGoals.find((g) => g.slug === slug)
    return rankedGoal && rankedGoal.rank
  }

  const updateRanking = (slug, rank) => {
    let rankToCompare = rank
    const newRankedGoals = rankedGoals.map((i) => {
      if (i.slug === slug) {
        return { ...i, rank }
      }
      if (i.rank === rankToCompare) {
        if (i.rank >= goals.length) {
          return { ...i, rank: null }
        }
        rankToCompare += 1
        return { ...i, rank: i.rank + 1 }
      }
      return i
    })
    setRankedGoals(newRankedGoals)
    updateResponse(exerciseIndex, 'rankedGoals', newRankedGoals)
    setContinueButtonDisabled(newRankedGoals.some((g) => g.rank === null))
  }

  const removeRanking = (slug) => {
    const newRankedGoals = rankedGoals.map((i) => {
      if (i.slug === slug) {
        return { ...i, rank: null }
      }
      return i
    })
    setRankedGoals(newRankedGoals)
    updateResponse(exerciseIndex, 'rankedGoals', newRankedGoals)
  }

  const handleDragStart = (e) => {
    const { active } = e
    const draggedGoalSlug = active.id
    const rank = getRank(draggedGoalSlug)
    if (rank) {
      setDragging(true)
    }
  }

  const handleDragEnd = (e) => {
    setDragging(false)
    const { active, over } = e
    if (active?.id && over?.id) {
      const draggedGoalSlug = active.id
      const rank = over.id
      updateRanking(draggedGoalSlug, rank)
    } else if (!over) {
      const draggedGoalSlug = active.id
      removeRanking(draggedGoalSlug, null)
    }
  }

  return (
    <div>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        autoScroll={false}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="order-last flex flex-col gap-4 md:order-first">
            {unrankedGoals.map((goal) => (
              <DraggableItem key={`draggable-${goal.slug}`} id={goal.slug}>
                {goal.name}
              </DraggableItem>
            ))}
          </div>
          <div className="order-first flex flex-col gap-4 md:order-last">
            {goals.map((goal, idx) => (
              <DroppableItem
                key={`droppable-container-${idx + 1}-${goal}`}
                rankNumber={idx + 1}
                getRankedGoal={getRankedGoal}
                dragging={dragging}
              >
                <div className="flex items-center justify-center font-roboto">
                  {idx + 1 === 1 && 'Most Important '}
                  {idx + 1 === goals.length && 'Least Important '}
                </div>
              </DroppableItem>
            ))}
          </div>
        </div>
      </DndContext>
    </div>
  )
}

const DroppableItem = ({ rankNumber, getRankedGoal, dragging, children }) => {
  const droppableClasses = cn(
    'flex h-10 justify-center rounded-[15px] border border-dashed border-intoo-blue-medium text-intoo-blue-medium',
  )

  const rankedGoal = getRankedGoal(rankNumber)
  if (rankedGoal)
    return (
      <Droppable
        key={`ranked-draggable-${rankNumber}`}
        id={rankNumber}
        className={cn(dragging ? droppableClasses : 'w-full')}
      >
        <DraggableItem
          id={rankedGoal.slug}
          className={'w-full bg-intoo-blue-medium text-white'}
        >
          {rankedGoal.name}
        </DraggableItem>
      </Droppable>
    )
  return (
    <Droppable className={droppableClasses} id={rankNumber}>
      {children}
    </Droppable>
  )
}

const DraggableItem = ({ className, id, children }) => {
  return (
    <Draggable
      className={cn(
        'rounded-[15px] border-2 border-intoo-blue-medium bg-white font-roboto capitalize text-intoo-blue-medium hover:bg-intoo-blue-medium hover:text-white',
        className,
      )}
      id={id}
    >
      {children}
    </Draggable>
  )
}
